import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  IconAction,
  Input,
  Loading,
  Modal,
  Select,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { IconPencil, IconPlus, IconRefresh, IconX } from '@/components/icons';
import { formatDateTime } from '@/lib/format';

interface PortalUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: 'ACTIVE' | 'INVITED' | 'DISABLED';
  last_login_at: string | null;
  locked_until: string | null;
  failed_logins: number;
  has_password: boolean;
}

const ROLES = ['CLIENT_ADMIN', 'CLIENT_MANAGER', 'CLIENT_AGENT', 'VIEWER'];
const roleLabel = (r: string) => r.replace('CLIENT_', '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/**
 * Who can log in to this client's portal, and their passwords.
 *
 * A generated password is shown exactly once, here, with a copy button --
 * the server returns it once and keeps only the hash.
 */
export function ClientPortalAccess({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const key = ['client-portal-users', clientId];
  const { data, isPending, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: PortalUser[] }>(`/admin/clients/${clientId}/users`),
  });
  const [resetting, setResetting] = useState<PortalUser | null>(null);
  const [adding, setAdding] = useState(false);
  const [revealed, setRevealed] = useState<{ email: string; password: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const toggle = useMutation({
    mutationFn: (u: PortalUser) =>
      api.post(`/admin/clients/${clientId}/users/${u.id}/status`, {
        status: u.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED',
      }),
    onSuccess: refresh,
    onError: (err) => setMessage(errorMessage(err)),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const users = data.data;
  const locked = (u: PortalUser) => !!u.locked_until && new Date(u.locked_until) > new Date();

  return (
    <div className="space-y-4">
      <Card
        title="Portal logins"
        description="The usernames (emails) that can sign in to this client's portal. Setting a password signs that user out everywhere."
        padded={false}
        actions={
          <Button size="sm" icon={<IconPlus />} onClick={() => setAdding(true)}>
            Add login
          </Button>
        }
      >
        {users.length === 0 ? (
          <EmptyState title="Nobody can log in yet" description="Add a login for the client's administrator." />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>Username (email)</TH>
                  <TH>Name</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH>Last login</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {users.map((u) => (
                  <TR key={u.id}>
                    <TD className="font-mono text-xs text-ink-900 dark:text-ink-50">{u.email}</TD>
                    <TD>{u.name}</TD>
                    <TD>{roleLabel(u.role)}</TD>
                    <TD>
                      {u.status === 'DISABLED' ? (
                        <Badge tone="gray" dot>Disabled</Badge>
                      ) : locked(u) ? (
                        <Badge tone="red" dot>Locked out</Badge>
                      ) : !u.has_password ? (
                        <Badge tone="amber" dot>No password</Badge>
                      ) : (
                        <Badge tone="green" dot>Active</Badge>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap text-xs text-ink-500">
                      {u.last_login_at ? formatDateTime(u.last_login_at) : 'Never'}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        <IconAction label="Set or generate password" onClick={() => setResetting(u)}>
                          <IconPencil />
                        </IconAction>
                        <IconAction
                          label={u.status === 'DISABLED' ? 'Enable login' : 'Disable login'}
                          tone={u.status === 'DISABLED' ? 'default' : 'danger'}
                          busy={toggle.isPending && toggle.variables?.id === u.id}
                          onClick={() => toggle.mutate(u)}
                        >
                          {u.status === 'DISABLED' ? <IconRefresh /> : <IconX />}
                        </IconAction>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Card>
      {message && <p className="text-sm text-red-600 dark:text-red-400">{message}</p>}

      <PasswordModal
        clientId={clientId}
        user={resetting}
        onClose={() => setResetting(null)}
        onDone={(password) => {
          if (password && resetting) setRevealed({ email: resetting.email, password });
          setResetting(null);
          refresh();
        }}
      />
      <AddUserModal
        clientId={clientId}
        open={adding}
        onClose={() => setAdding(false)}
        onDone={(email, password) => {
          setAdding(false);
          if (password) setRevealed({ email, password });
          refresh();
        }}
      />
      <RevealModal revealed={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}

function PasswordModal({
  clientId,
  user,
  onClose,
  onDone,
}: {
  clientId: string;
  user: PortalUser | null;
  onClose: () => void;
  onDone: (generated: string | null) => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: { password?: string; generate?: boolean }) =>
      api.post<{ password: string | null }>(`/admin/clients/${clientId}/users/${user?.id}/password`, body),
    onSuccess: (r) => {
      setPassword('');
      setError(null);
      onDone(r.password);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Modal
      open={user !== null}
      onClose={() => {
        setPassword('');
        setError(null);
        onClose();
      }}
      title={`Password for ${user?.email ?? ''}`}
      description="Type one, or generate a random one to send them. Either way they are signed out of every device and their lockout is cleared."
      footer={
        <>
          <Button variant="secondary" onClick={() => save.mutate({ generate: true })} loading={save.isPending && !password}>
            Generate random
          </Button>
          <Button onClick={() => save.mutate({ password })} disabled={password.length < 12} loading={save.isPending && !!password}>
            Set this password
          </Button>
        </>
      }
    >
      <Field label="New password" hint="At least 12 characters.">
        <Input type="text" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </Modal>
  );
}

function AddUserModal({
  clientId,
  open,
  onClose,
  onDone,
}: {
  clientId: string;
  open: boolean;
  onClose: () => void;
  onDone: (email: string, password: string | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('CLIENT_ADMIN');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ password: string | null }>(`/admin/clients/${clientId}/users`, { email, name, role, generate: true }),
    onSuccess: (r) => {
      const added = email;
      setEmail('');
      setName('');
      setError(null);
      onDone(added, r.password);
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a portal login"
      description="A random password is generated and shown once, for you to send them."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!email || !name} loading={create.isPending}>
            Create login
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email (username)">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </Modal>
  );
}

function RevealModal({
  revealed,
  onClose,
}: {
  revealed: { email: string; password: string } | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(`Username: ${revealed.email}\nPassword: ${revealed.password}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Modal
      open={revealed !== null}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title="New password — shown once"
      description="Copy it now and send it to them. It is not stored anywhere it can be shown again."
      footer={
        <>
          <Button variant="secondary" onClick={copy}>
            {copied ? 'Copied' : 'Copy username & password'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-xs text-ink-500">Username</dt>
          <dd className="mt-0.5 font-mono text-ink-900 dark:text-ink-50">{revealed?.email}</dd>
        </div>
        <div>
          <dt className="text-xs text-ink-500">Password</dt>
          <dd className="mt-0.5 select-all rounded-md bg-sunken px-3 py-2 font-mono text-base tracking-wide text-ink-900 dark:text-ink-50">
            {revealed?.password}
          </dd>
        </div>
      </dl>
    </Modal>
  );
}
